import { useState } from "react";
import { Col, Button } from "react-bootstrap";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, FormProvider } from "react-hook-form";
import { Breakpoint } from "react-socks";

import Dropdown from "components/atoms/Dropdown/Dropdown";
import usePermissions from "hooks/usePermissions";
import Card from "components/molecules/Card/Card";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import { ReactComponent as BoltIcon } from "assets/images/icons/Bolt.svg";
import { ReactComponent as CalculatorIcon } from "assets/images/icons/Calculator.svg";
import { ReactComponent as FileInvoiceDollarIcon } from "assets/images/icons/FileInvoiceDollar.svg";
import { ReactComponent as ShipIcon } from "assets/images/icons/Ship.svg";
import StatusWidget from "../../widgets/StatusWidget";
import "./WidgetBuilder.scss";

const StatusWidgetBuilder = function ({
  statusWidgetData,
  widgetDataSource,
  widgetContent,
  handleWidgetContentClick,
  handleWidgetDataSourceClick,
  setWidgetFilters,
  widgetFilters,
  refreshToken,
}) {
  const [activeKey, setActiveKey] = useState("");

  const { permissions: permissionsUnparsed } = usePermissions();
  const intl = useIntl();
  const methods = useForm();

  const permissions = permissionsUnparsed ? JSON.parse(permissionsUnparsed) : null;
  const paymentLocations = permissions?.payment_location?.locations.sort((a, b) => a.label.localeCompare(b.label));
  paymentLocations?.unshift({
    label: intl.formatMessage({
      id: "general.all",
      defaultMessage: "All",
    }),
    value: "all",
  });
  const { tracking, quoting, invoicing } = permissions.permissions;

  const shipperDataSourceButtons = [
    ...(permissions?.menu_items?.view_quick_requests
      ? [
          {
            name: "quick_request",
            id: "general.quickRequests",
            defaultMessage: "Quick requests",
            label: "Quick requests",
            icon: <BoltIcon />,
            available: true,
            content: [
              {
                name: "Open quick requests",
                id: "dashboard.widgetDialog.statusWidgetBuilder.open",
                defaultMessage: "Open",
              },
            ],
          },
        ]
      : []),
    ...(permissions?.menu_items?.view_quote_requests
      ? [
          {
            name: "quote_request",
            id: "general.quoteRequests",
            defaultMessage: "Quote requests",
            label: "Quote requests",
            icon: <CalculatorIcon />,
            available: quoting?.view_quote_requests,
            content: [
              {
                name: "Open quote requests",
                id: "dashboard.widgetDialog.statusWidgetBuilder.open",
                defaultMessage: "Open",
              },
              {
                name: "Awaiting approval",
                id: "dashboard.widgetDialog.statusWidgetBuilder.awaitingApproval",
                defaultMessage: "Awaiting approval",
              },
              {
                name: "At risk for cancel",
                id: "dashboard.widgetDialog.statusWidgetBuilder.atRiskOfCancel",
                defaultMessage: "At risk of cancel",
                // TODO: temporary disabled until the query is fixed
                disabled: true,
              },
              {
                name: "Awaiting booking",
                id: "dashboard.widgetDialog.statusWidgetBuilder.awaitingBooking",
                defaultMessage: "Awaiting booking",
              },
            ],
          },
        ]
      : []),
    ...(permissions?.menu_items?.view_shipments
      ? [
          {
            name: "shipment",
            id: "general.shipments",
            defaultMessage: "Shipments",
            label: "Shipments",
            icon: <ShipIcon />,
            available: tracking?.view_shipments,
            content: [
              {
                name: "Shipments, in transit",
                id: "general.inTransit",
                defaultMessage: "In transit",
              },
              {
                name: "Incomplete shipments",
                id: "general.incomplete",
                defaultMessage: "Incomplete",
              },
              {
                name: "Delivered shipments",
                id: "general.delivered",
                defaultMessage: "Delivered",
              },
              {
                name: "Processed shipments",
                id: "dashboard.widgetDialog.statusWidgetBuilder.processed",
                defaultMessage: "Processed",
              },
              {
                name: "Booked, year to date",
                id: "dashboard.widgetDialog.statusWidgetBuilder.bookedYearToDate",
                defaultMessage: "Booked, year to date",
              },
              {
                name: "Delayed Shipments",
                id: "dashboard.widgetDialog.statusWidgetBuilder.delayed",
                defaultMessage: "Delayed Shipments",
              },
            ],
          },
        ]
      : []),
    ...(permissions?.menu_items?.view_invoices
      ? [
          {
            name: "invoice",
            id: "general.invoices",
            defaultMessage: "Invoices",
            icon: <FileInvoiceDollarIcon />,
            available: invoicing?.view_invoices && invoicing?.audit_invoices,
            content: [
              {
                name: "Awaiting audit",
                id: "dashboard.widgetDialog.statusWidgetBuilder.awaitingAudit",
                defaultMessage: "Awaiting audit",
              },
            ],
          },
        ]
      : []),
  ];

  const providerDataSourceButtons = [
    ...(quoting?.view_quote_requests
      ? [
          {
            name: "quote_request",
            id: "general.quoteRequests",
            defaultMessage: "Quote requests",
            label: "Quote requests",
            icon: <CalculatorIcon />,
            available: quoting?.view_quote_requests,
            content: [
              {
                name: "Open quote requests",
                id: "dashboard.widgetDialog.statusWidgetBuilder.open",
                defaultMessage: "Open",
              },
              {
                name: "At risk for cancel",
                id: "dashboard.widgetDialog.statusWidgetBuilder.atRiskOfCancel",
                defaultMessage: "At risk of cancel",
                // TODO: temporary disabled until the query is fixed
                disabled: true,
              },
              {
                name: "Awaiting booking",
                id: "dashboard.widgetDialog.statusWidgetBuilder.awaitingBooking",
                defaultMessage: "Awaiting booking",
              },
            ],
          },
        ]
      : []),
    ...(tracking?.view_shipments
      ? [
          {
            name: "shipment",
            id: "general.shipments",
            defaultMessage: "Shipments",
            label: "Shipments",
            icon: <ShipIcon />,
            available: tracking?.view_shipments,
            content: [
              {
                name: "Shipments, in transit",
                id: "general.inTransit",
                defaultMessage: "In transit",
              },
              {
                name: "Incomplete shipments",
                id: "general.incomplete",
                defaultMessage: "Incomplete",
              },
              {
                name: "Delivered shipments",
                id: "general.delivered",
                defaultMessage: "Delivered",
              },
              {
                name: "Processed shipments",
                id: "dashboard.widgetDialog.statusWidgetBuilder.processed",
                defaultMessage: "Processed",
              },
              {
                name: "Booked, year to date",
                id: "dashboard.widgetDialog.statusWidgetBuilder.bookedYearToDate",
                defaultMessage: "Booked, year to date",
              },
              {
                name: "Delayed Shipments",
                id: "dashboard.widgetDialog.statusWidgetBuilder.delayed",
                defaultMessage: "Delayed Shipments",
              },
              {
                name: "Pending POD shipments",
                id: "dashboard.widgetDialog.statusWidgetBuilder.pendingPODShipments",
                defaultMessage: "Pending POD",
              },
              {
                name: "Pending Tracking Number",
                id: "dashboard.widgetDialog.statusWidgetBuilder.pendingTrackingNumber",
                defaultMessage: "Pending tracking number",
              },
            ],
          },
        ]
      : []),
    ...(invoicing?.view_invoices
      ? [
          {
            name: "invoice",
            id: "general.invoices",
            defaultMessage: "Invoices",
            label: "Invoices",
            icon: <FileInvoiceDollarIcon />,
            available: invoicing?.view_invoices,
            content: [
              {
                name: "Awaiting audit Invoices",
                id: "billing.awaitingAudit",
                defaultMessage: "Awaiting audit",
              },
            ],
          },
        ]
      : []),
  ];

  const dataSourceButtons = permissions.user_type === "shipper" ? shipperDataSourceButtons : providerDataSourceButtons;

  const handlePaymentlocation = (item) => {
    if (item[0]?.value === "all") {
      const { payment_location, ...newWidgetFilters } = widgetFilters;
      setWidgetFilters(newWidgetFilters);
    } else {
      setWidgetFilters({ ...widgetFilters, payment_location: item[0]?.value });
    }
  };

  return (
    <div className="d-flex flex-column flex-md-row">
      <FormProvider {...methods}>
        <Col className="choose-widget-column">
          <p className="choose-widget-title">
            <FormattedMessage id="general.chooseDataSource" defaultMessage="Choose data source" />
          </p>
          <Breakpoint large up medium>
            {dataSourceButtons.map((button) => {
              if (!button.available) return;
              return (
                <Button
                  className={`mb-3 d-flex flex-row align-items-center ${
                    widgetDataSource !== button.name && "list-widget-button"
                  }`}
                  onClick={() => handleWidgetDataSourceClick(button.name)}
                  style={{
                    justifyContent: "flex-start !important",
                    borderRadius: "26px",
                    border: "none",
                    backgroundColor: widgetDataSource === button.name ? "#000000" : "rgba(0, 0, 0, 0)",
                    color: widgetDataSource === button.name ? "#FFFFFF" : "#000000",
                    padding: "7px 0px",
                    width: "150px",
                    height: "32px",
                  }}
                >
                  <div
                    className="mr-3 ml-3"
                    style={{
                      filter: widgetDataSource === button.name ? "invert(100%) " : "none",
                    }}
                  >
                    {button.icon}
                  </div>
                  <span className="widget-button-text">
                    <FormattedMessage id={button.id} defaultMessage={button.defaultMessage} />
                  </span>
                </Button>
              );
            })}
          </Breakpoint>
          <div>
            <Breakpoint small down>
              <Dropdown
                value={activeKey}
                options={dataSourceButtons}
                onChange={(val) => {
                  setActiveKey(val.label);
                  handleWidgetDataSourceClick(val.name);
                }}
              />
            </Breakpoint>
          </div>
        </Col>

        <Col className="choose-widget-column mt-3 mt-md-0">
          <p className={`choose-widget-title ${!widgetDataSource && "text-secondary"}`}>
            <FormattedMessage id="general.chooseWidgetContent" defaultMessage="Choose widget content" />
          </p>

          {dataSourceButtons
            .filter((button) => button.name === widgetDataSource)
            .map((button) => (
              <div>
                {!button.content && (
                  <p className="text-secondary w-75 m-0 p-0">
                    <FormattedMessage
                      id="dashboard.widgetDialog.statusWidgetBuilder.noWidgetContentAvailable"
                      defaultMessage="No widget content available for {widgetDataSource} yet."
                      values={{ widgetDataSource: button.defaultMessage.toLowerCase() }}
                    />
                  </p>
                )}
                {button.content &&
                  button.content.map((content) => (
                    <Button
                      className={`mb-3 d-flex flex-row align-items-center justify-content-center ${
                        widgetContent !== content.name && "list-widget-button"
                      }`}
                      onClick={() => handleWidgetContentClick(content.name)}
                      disabled={content.disabled}
                      style={{
                        borderRadius: "26px",
                        padding: "7px 0px",
                        width: "145px",
                        height: "32px",
                        border: "1px solid #000000",
                        color: widgetContent === content.name ? "#FFFFFF" : "#000000",
                        backgroundColor: widgetContent === content.name ? "#000000" : "rgba(0, 0, 0, 0)",
                      }}
                    >
                      <span className="widget-button-text">
                        <FormattedMessage id={content.id} defaultMessage={content.defaultMessage} />
                      </span>
                    </Button>
                  ))}
              </div>
            ))}
        </Col>
        {paymentLocations?.length > 0 && (
          <Col className="choose-widget-column">
            <p className={`choose-widget-title ${!widgetContent && "text-secondary"}`}>
              <FormattedMessage id="general.filterContentBy" defaultMessage="Filter content by" />
            </p>
            {widgetContent && (
              <Col className="choose-widget-column">
                <div className="w-136-px">
                  <SearchSelect
                    defaultValue={widgetFilters?.payment_location || "all"}
                    options={paymentLocations}
                    name="payment_location"
                    onSelect={handlePaymentlocation}
                  />
                </div>
              </Col>
            )}
          </Col>
        )}

        <Col className="preview-widget-column mt-3 mt-md-0">
          <p className={`choose-widget-title ${!widgetContent && "text-secondary"}`}>
            <FormattedMessage id="general.preview" defaultMessage="Preview" />
          </p>
          {widgetContent && (
            <div style={{ border: "16px solid #F1F3F4", borderRadius: "24px" }}>
              <Card className="flex-grow-0 p-0">
                <StatusWidget
                  statusWidgetData={statusWidgetData}
                  loadingWidget={!statusWidgetData}
                  widgetContent={widgetContent}
                  paymentLocation={widgetFilters?.payment_location}
                  isLayoutLocked
                  isPreview
                  refreshToken={refreshToken}
                />
              </Card>
            </div>
          )}
        </Col>
      </FormProvider>
    </div>
  );
};

StatusWidgetBuilder.propTypes = {
  statusWidgetData: PropTypes.instanceOf(Object).isRequired,
  widgetDataSource: PropTypes.string.isRequired,
  widgetContent: PropTypes.string.isRequired,
  handleWidgetContentClick: PropTypes.func.isRequired,
  handleWidgetDataSourceClick: PropTypes.func.isRequired,
  setWidgetFilters: PropTypes.func.isRequired,
  widgetFilters: PropTypes.instanceOf(Object).isRequired,
  refreshToken: PropTypes.bool.isRequired,
};

export default StatusWidgetBuilder;
